import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Col, Empty, Form, InputNumber, Modal, Row, Select, Spin, Table, Tooltip, Typography, message } from 'antd';
import { CalculatorOutlined, DownOutlined, ExclamationCircleFilled, InfoCircleOutlined, RightOutlined } from '@ant-design/icons';
import { orderProfitEstimationApi } from '../../../../services/process/order-profit-estimation';
import type {
  ProfitEstimationCalculateResponse,
  ProfitEstimationInitResponse,
  PurchaseQuoteComponent,
} from '../../../../types/process/order-profit-estimation';
import {
  buildTooltipContent,
  getCalculationIssueWarning,
  money,
  percent,
  PURCHASE_DEVICE_TYPE_SELF,
} from './profitEstimationModal.helpers';

const { Text } = Typography;

interface ProfitEstimationModalProps {
  orderNo: string;
  visible: boolean;
  onClose: () => void;
  onDecision?: () => void;
}

type MetricRow = {
  key: string;
  label: React.ReactNode;
  kind: 'group' | 'single' | 'dualLabel' | 'dualValue';
  values?: number[];
  dualValues?: Array<{ before: number; after: number }>;
  tone?: 'group' | 'muted' | 'profit';
};

const PROFIT_SECTION_BG = '#eef5ff';
const PROFIT_BORDER = '#d8e6ff';
const BEFORE_COLOR = '#1677ff';
const AFTER_COLOR = '#fa8c16';
const PROFIT_VALUE_COLOR = '#ff4d4f';
const UNAVAILABLE_PURCHASE_TYPE_WARNING = '当前采购设备类型暂无对应采购价，请选择其他设备类型';

const renderInventoryPrice = (component: PurchaseQuoteComponent) => {
  if (!component.hasQuote || component.devicePrice <= 0) {
    return '-';
  }
  return `${money(component.devicePrice)}（${component.invoiceTypeText || '-'}）`;
};

const resolveAvailablePurchaseType = (data: ProfitEstimationInitResponse) => {
  const preferred = data.purchaseDeviceOptions.find(
    (item) => item.value === data.defaultPurchaseType && !item.disabled && item.value !== PURCHASE_DEVICE_TYPE_SELF
  );
  if (preferred) {
    return preferred.value;
  }
  return (
    data.purchaseDeviceOptions.find((item) => !item.disabled && item.value !== PURCHASE_DEVICE_TYPE_SELF)?.value
    ?? data.purchaseDeviceOptions.find((item) => item.value === data.defaultPurchaseType && !item.disabled)?.value
    ?? data.purchaseDeviceOptions.find((item) => !item.disabled)?.value
  );
};

const getMaxPrepaymentPeriods = (data: ProfitEstimationInitResponse | null) => {
  const totalPeriods = data?.orderSummary.totalPeriods ?? 0;
  return Math.max(totalPeriods - 1, 0);
};

const clampPrepaymentPeriods = (value: number | undefined, data: ProfitEstimationInitResponse | null) => {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return undefined;
  }
  return Math.min(Math.max(value, 0), getMaxPrepaymentPeriods(data));
};


const ProfitEstimationModal: React.FC<ProfitEstimationModalProps> = ({ orderNo, visible, onClose, onDecision }) => {
  const [form] = Form.useForm();
  const autoCalculatedRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [initData, setInitData] = useState<ProfitEstimationInitResponse | null>(null);
  const [calcData, setCalcData] = useState<ProfitEstimationCalculateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [calculationIssueWarning, setCalculationIssueWarning] = useState<string | null>(null);
  const [sectionOpen, setSectionOpen] = useState<Record<string, boolean>>({
    income: true,
    cost: true,
    prepay: true,
  });
  const [otherCostOpen, setOtherCostOpen] = useState(false);

  useEffect(() => {
    autoCalculatedRef.current = false;
  }, [visible, orderNo]);

  useEffect(() => {
    if (!visible || !orderNo) return;

    setLoading(true);
    setError(null);
    setInitData(null);
    setCalcData(null);
    setCalculationIssueWarning(null);

    orderProfitEstimationApi
      .getInit(orderNo)
      .then((res) => {
        if (res.code !== 200 || !res.data) {
          throw new Error(res.message || '加载失败');
        }
        setInitData(res.data);
        const availablePurchaseType = resolveAvailablePurchaseType(res.data);
        form.setFieldsValue({
          purchaseDeviceType: availablePurchaseType,
          prepaymentPeriods: clampPrepaymentPeriods(res.data.prepaymentPeriods, res.data),
          maturityBuyoutPrice: res.data.orderSummary.maturityBuyoutPrice || undefined,
        });
      })
      .catch((e: any) => setError(e?.message || '加载收益测算失败'))
      .finally(() => setLoading(false));
  }, [visible, orderNo, form]);

  const purchaseType = Form.useWatch('purchaseDeviceType', form);
  const isSelfPurchase = purchaseType === PURCHASE_DEVICE_TYPE_SELF;
  const inventoryGroups = initData?.inventoryGroups ?? [];
  const purchaseOptions = initData?.purchaseDeviceOptions ?? [];
  const selectedPurchaseOption = useMemo(
    () => purchaseOptions.find((item) => item.value === purchaseType),
    [purchaseOptions, purchaseType]
  );
  const maxPrepaymentPeriods = useMemo(() => getMaxPrepaymentPeriods(initData), [initData]);
  const resultPeriods = calcData?.result?.periods ?? [];
  const diagnostics = calcData?.diagnostics ?? initData?.diagnostics;
  const activeWarnings = useMemo(() => {
    const warnings = [...(calcData?.warnings ?? initData?.warnings ?? [])];
    if (calculationIssueWarning) {
      warnings.push(calculationIssueWarning);
    }
    return Array.from(new Set(warnings));
  }, [calcData?.warnings, initData?.warnings, calculationIssueWarning]);
  const issueItems = diagnostics?.items.filter((item) => item.status !== 'ok') ?? [];
  const hasIssueNotice = activeWarnings.length > 0 || issueItems.length > 0;
  const showAfterCells = (calcData?.result?.prepaymentRent ?? 0) > 0;
  const [issueModalOpen, setIssueModalOpen] = useState(false);

  const submitCalculation = async (values: any, options?: { silent?: boolean }) => {
    setCalculating(true);
    try {
      const res = await orderProfitEstimationApi.calculate(orderNo, {
        purchaseDeviceType: values.purchaseDeviceType,
        selfPurchasePrice: values.selfPurchasePrice,
        maturityBuyoutPrice: values.maturityBuyoutPrice,
        prepaymentPeriods: values.prepaymentPeriods,
      });
      if (res.code !== 200 || !res.data) {
        throw new Error(res.message || '计算失败');
      }
      setCalculationIssueWarning(null);
      setCalcData(res.data);
      return true;
    } catch (e: any) {
      const issueWarning = getCalculationIssueWarning(e?.message);
      setCalculationIssueWarning(issueWarning);
      if (!options?.silent) {
        message.error(e?.message || '计算失败');
      }
      return false;
    } finally {
      setCalculating(false);
    }
  };

  const canAutoCalculate = (values: any, data: ProfitEstimationInitResponse | null) => {
    if (!data) return false;
    if (!values?.purchaseDeviceType && !data.defaultPurchaseType) return false;
    if (data.purchaseDeviceOptions.some((item) => item.value === values?.purchaseDeviceType && item.disabled)) return false;
    if (values?.prepaymentPeriods === undefined || values?.prepaymentPeriods === null) return false;
    if (values?.purchaseDeviceType === PURCHASE_DEVICE_TYPE_SELF && (values?.selfPurchasePrice === undefined || values?.selfPurchasePrice === null)) {
      return false;
    }
    if (data.orderSummary.requiresManualMaturityBuyoutPrice && (values?.maturityBuyoutPrice === undefined || values?.maturityBuyoutPrice === null)) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (!visible || !initData || loading || calculating || autoCalculatedRef.current) {
      return;
    }

    const values = form.getFieldsValue();
    if (!canAutoCalculate(values, initData)) {
      return;
    }

    autoCalculatedRef.current = true;
    void submitCalculation(values, { silent: true });
  }, [visible, initData, loading, calculating, form]);

  const handleCalculate = async () => {
    if (selectedPurchaseOption?.disabled) {
      setCalculationIssueWarning(UNAVAILABLE_PURCHASE_TYPE_WARNING);
      message.warning(UNAVAILABLE_PURCHASE_TYPE_WARNING);
      return;
    }
    try {
      const values = await form.validateFields();
      await submitCalculation(values);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '计算失败');
    }
  };

  const periodResults = calcData?.result?.periods ?? [];

  const rows = useMemo<MetricRow[]>(() => {
    if (!calcData) return [];

    const periods = periodResults;
    const incomeOpen = sectionOpen.income;
    const costOpen = sectionOpen.cost;
    const prepayOpen = sectionOpen.prepay;

    const items: MetricRow[] = [];

    items.push({
      key: 'income-group',
      label: (
        <span style={{ cursor: 'pointer' }} onClick={() => setSectionOpen((prev) => ({ ...prev, income: !prev.income }))}>
          {incomeOpen ? <DownOutlined style={{ marginRight: 6, fontSize: 12 }} /> : <RightOutlined style={{ marginRight: 6, fontSize: 12 }} />}
          收入
        </span>
      ),
      kind: 'group',
      tone: 'group',
      values: periods.map((item) => item.prepay.totalIncome),
    });
      if (incomeOpen) {
      items.push({
        key: 'base-rent',
        label: '累计常规租金',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.baseRent),
      });
      items.push({
        key: 'residual',
        label: (
          <span>
            设备回收残值
            <Tooltip title="当前期设备回收残值">
              <InfoCircleOutlined style={{ marginLeft: 4, color: '#999' }} />
            </Tooltip>
          </span>
        ),
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.residualValue),
      });
      items.push({
        key: 'penalty',
        label: '违约金',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.penaltyFee),
      });
    }

    items.push({
      key: 'cost-group',
      label: (
        <span style={{ cursor: 'pointer' }} onClick={() => setSectionOpen((prev) => ({ ...prev, cost: !prev.cost }))}>
          {costOpen ? <DownOutlined style={{ marginRight: 6, fontSize: 12 }} /> : <RightOutlined style={{ marginRight: 6, fontSize: 12 }} />}
          成本
        </span>
      ),
      kind: 'group',
      tone: 'group',
      values: periods.map((item) => item.prepay.totalCost),
    });
    if (costOpen) {
      items.push({
        key: 'purchase-price',
        label: '设备采购价',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.purchasePrice),
      });
      items.push({
        key: 'other-cost',
        label: (
          <span style={{ cursor: 'pointer' }} onClick={() => setOtherCostOpen((prev) => !prev)}>
            {otherCostOpen ? <DownOutlined style={{ marginRight: 6, fontSize: 12 }} /> : <RightOutlined style={{ marginRight: 6, fontSize: 12 }} />}
            其他合计成本
          </span>
        ),
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.otherCostTotal),
      });
      if (otherCostOpen) {
        items.push({
          key: 'lock-fee',
          label: '上锁费',
          kind: 'single',
          tone: 'muted',
          values: periods.map(() => calcData.costBreakdown.lockFee),
        });
        if ((calcData.costBreakdown.courierFee || 0) > 0) {
          items.push({
            key: 'courier-fee',
            label: '快递费',
            kind: 'single',
            tone: 'muted',
            values: periods.map((_, index) => (index === periods.length - 1 ? calcData.costBreakdown.courierFee : 0)),
          });
        }
        items.push({
          key: 'shipping-fee',
          label: '发货费',
          kind: 'single',
          tone: 'muted',
          values: periods.map(() => calcData.costBreakdown.shippingFee),
        });
        items.push({
          key: 'inspection-fee',
          label: '平台检测费',
          kind: 'single',
          tone: 'muted',
          values: periods.map(() => calcData.costBreakdown.inspectionFee),
        });
        items.push({
          key: 'risk-fee',
          label: '风控费',
          kind: 'single',
          tone: 'muted',
          values: periods.map(() => calcData.costBreakdown.riskControlFee),
        });
        items.push({
          key: 'accessory-fee',
          label: '配件费',
          kind: 'single',
          tone: 'muted',
          values: periods.map(() => calcData.costBreakdown.accessoryFee),
        });
      }
      items.push({
        key: 'rent-commission',
        label: '租金抽成费用',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.rentCommissionFee),
      });
      items.push({
        key: 'penalty-commission',
        label: '违约金抽成费用',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.penaltyCommissionFee),
      });
    }

    items.push({
      key: 'prepay-group',
      label: (
        <span style={{ cursor: 'pointer' }} onClick={() => setSectionOpen((prev) => ({ ...prev, prepay: !prev.prepay }))}>
          {prepayOpen ? <DownOutlined style={{ marginRight: 6, fontSize: 12 }} /> : <RightOutlined style={{ marginRight: 6, fontSize: 12 }} />}
          预收
        </span>
      ),
      kind: 'group',
      tone: 'group',
      values: periods.map((item) => item.prepay.prepaymentRent || 0),
    });
    if (prepayOpen) {
      items.push({
        key: 'prepay-rent',
        label: '预收租金',
        kind: 'single',
        tone: 'muted',
        values: periods.map((item) => item.prepay.prepaymentRent || 0),
      });
    }

      items.push({
        key: 'profit-group',
        label: '收益利润',
        kind: 'dualLabel',
        tone: 'group',
    });
    items.push({
      key: 'gross-profit',
      label: '毛利',
      kind: 'dualValue',
      tone: 'profit',
      dualValues: periods.map((item) => ({
        before: item.nonPrepay.grossProfit,
        after: item.prepay.grossProfit,
      })),
    });
    items.push({
      key: 'gross-margin',
      label: '毛利率',
      kind: 'dualValue',
      tone: 'profit',
      dualValues: periods.map((item) => ({
        before: item.nonPrepay.grossMargin,
        after: item.prepay.grossMargin,
      })),
    });

    return items;
  }, [calcData, periodResults, sectionOpen, otherCostOpen]);

  const columns = useMemo<any[]>(() => {
    const base: any[] = [
      {
        title: '收益测算项目',
        dataIndex: 'label',
        key: 'label',
        width: 180,
        fixed: 'left' as const,
        render: (value: React.ReactNode, record: MetricRow) => (
          <div
            style={{
              fontWeight: record.tone === 'group' ? 600 : 400,
              background: record.tone === 'profit' ? PROFIT_SECTION_BG : undefined,
              padding: record.tone === 'profit' ? '14px 12px' : undefined,
              minHeight: record.tone === 'profit' ? 52 : undefined,
              display: 'flex',
              alignItems: 'center',
              justifyContent: record.tone === 'profit' ? 'center' : 'flex-start',
              borderTop: record.tone === 'profit' ? `1px solid ${PROFIT_BORDER}` : undefined,
            }}
          >
            {value}
          </div>
        ),
      },
    ];

    return base.concat(
      periodResults.map((item, index): any => ({
        title: `第${item.period}期`,
        key: `period-${item.period}`,
        width: 160,
        align: 'center',
        render: (_: unknown, record: MetricRow) => {
          const tooltip = calcData
            ? buildTooltipContent(record.key, index, calcData, {
                diagnostics,
                purchaseDeviceType: calcData.inputEcho.purchaseDeviceType,
                productQuality: initData?.orderSummary.productQuality,
              })
            : '';
          if (record.kind === 'group') {
            return (
              <Tooltip title={tooltip} mouseEnterDelay={0.2}>
                <div style={{ textAlign: 'center' }}>
                  <Text strong>{money(record.values?.[index])}</Text>
                </div>
              </Tooltip>
            );
          }
          if (record.kind === 'single') {
            return (
              <Tooltip title={tooltip} mouseEnterDelay={0.2}>
                <div style={{ textAlign: 'center' }}>
                  <Text style={{ color: '#666' }}>{money(record.values?.[index])}</Text>
                </div>
              </Tooltip>
            );
          }
          if (record.kind === 'dualLabel') {
            return (
              <Tooltip title={tooltip} mouseEnterDelay={0.2}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: showAfterCells ? '1fr 1fr' : '1fr',
                    background: PROFIT_SECTION_BG,
                    padding: 0,
                    width: '100%',
                    minHeight: 40,
                    borderTop: `1px solid ${PROFIT_BORDER}`,
                    borderLeft: `1px solid ${PROFIT_BORDER}`,
                    borderRight: `1px solid ${PROFIT_BORDER}`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 4px' }}>
                    <Text style={{ color: BEFORE_COLOR, fontSize: 12, fontWeight: 500 }}>预收前</Text>
                  </div>
                  {showAfterCells && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 4px', borderLeft: `1px solid ${PROFIT_BORDER}` }}>
                    <Text style={{ color: AFTER_COLOR, fontSize: 12, fontWeight: 500 }}>预收后</Text>
                  </div>
                  )}
                </div>
              </Tooltip>
            );
          }
          if (record.kind === 'dualValue') {
            const value = record.dualValues?.[index];
            return (
              <Tooltip title={tooltip} mouseEnterDelay={0.2}>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: showAfterCells ? '1fr 1fr' : '1fr',
                    background: PROFIT_SECTION_BG,
                    padding: 0,
                    width: '100%',
                    minHeight: 48,
                    borderTop: `1px solid ${PROFIT_BORDER}`,
                    borderLeft: `1px solid ${PROFIT_BORDER}`,
                    borderRight: `1px solid ${PROFIT_BORDER}`,
                    boxSizing: 'border-box',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 6px' }}>
                    <Text
                      style={{
                        color: PROFIT_VALUE_COLOR,
                        fontSize: record.key === 'gross-margin' ? 12 : 13,
                        lineHeight: 1.15,
                        wordBreak: 'break-all',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {record.key === 'gross-margin' ? percent(value?.before) : money(value?.before)}
                    </Text>
                  </div>
                  {showAfterCells && (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px 6px', borderLeft: `1px solid ${PROFIT_BORDER}` }}>
                    <Text
                      style={{
                        color: PROFIT_VALUE_COLOR,
                        fontSize: record.key === 'gross-margin' ? 12 : 13,
                        lineHeight: 1.15,
                        wordBreak: 'break-all',
                        textAlign: 'center',
                        fontWeight: 600,
                      }}
                    >
                      {record.key === 'gross-margin' ? percent(value?.after) : money(value?.after)}
                    </Text>
                  </div>
                  )}
                </div>
              </Tooltip>
            );
          }
          return '-';
        },
      }))
    );
  }, [periodResults, showAfterCells, calcData, diagnostics, initData?.orderSummary.productQuality]);

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>
            <CalculatorOutlined style={{ marginRight: 6, color: '#1677ff' }} />
            订单收益测算
          </span>
          {hasIssueNotice && (
            <Tooltip title="查看数据提示">
              <ExclamationCircleFilled
                style={{ color: '#fa8c16', cursor: 'pointer', fontSize: 16 }}
                onClick={(event) => {
                  event.stopPropagation();
                  setIssueModalOpen(true);
                }}
              />
            </Tooltip>
          )}
        </div>
      }
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1320}
    >
      {loading && <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />}

      {!loading && error && <Alert type="error" message={error} />}

      <Modal
        title="数据提示"
        open={issueModalOpen}
        onCancel={() => setIssueModalOpen(false)}
        footer={null}
        width={640}
        destroyOnClose
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {activeWarnings.length > 0 && (
            <Alert
              type="warning"
              message="提示信息"
              description={activeWarnings.map((warning) => (
                <div key={warning}>{warning}</div>
              ))}
            />
          )}
          {issueItems.length > 0 && (
            <Alert
              type={diagnostics?.hasBlockingIssue ? 'error' : 'warning'}
              message="部分数据缺失或已回退"
              description={issueItems.map((item) => (
                <div key={`${item.key}-${item.status}`}>{`${item.key}: ${item.message}`}</div>
              ))}
            />
          )}
          {!activeWarnings.length && !issueItems.length && (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无数据提示" />
          )}
        </div>
      </Modal>

      {!loading && !error && initData && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <Card title="平台库存" styles={{ body: { padding: 20 } }}>
            <Row gutter={[16, 16]}>
              {inventoryGroups.map((group) => (
                <Col span={12} key={group.groupKey}>
                  <div
                    style={{
                      border: `1px solid ${group.groupKey.includes('activate') ? '#f0b429' : '#2f6bff'}`,
                      borderRadius: 8,
                      padding: 16,
                      background: group.groupKey.includes('activate') ? '#fffaf0' : '#f7faff',
                    }}
                  >
                    <div style={{ marginBottom: 14, color: group.groupKey.includes('activate') ? '#f0b429' : '#2f6bff', fontWeight: 600 }}>
                      • {group.groupLabel}
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: '12px 14px' }}>
                        <Text style={{ color: '#666' }}>有锁设备采购价格：</Text>
                        <Text strong style={{ color: group.groupKey.includes('activate') ? '#f0b429' : '#2f6bff' }}>
                          {renderInventoryPrice(group.lock)}
                        </Text>
                      </div>
                      <div style={{ flex: 1, background: '#fff', borderRadius: 8, padding: '12px 14px' }}>
                        <Text style={{ color: '#666' }}>无锁设备采购价格：</Text>
                        <Text strong style={{ color: group.groupKey.includes('activate') ? '#f0b429' : '#2f6bff' }}>
                          {renderInventoryPrice(group.unlock)}
                        </Text>
                      </div>
                    </div>
                  </div>
                </Col>
              ))}
              {inventoryGroups.length === 0 && <Col span={24}><Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无库存报价" /></Col>}
            </Row>
          </Card>

          <Card title="订单信息" styles={{ body: { padding: 20 } }}>
            <Form form={form} layout="vertical">
              <Row gutter={16} align="bottom">
                <Col span={6}>
                  <Form.Item
                    label="采购设备类型"
                    name="purchaseDeviceType"
                    rules={[{ required: true, message: '请选择采购设备类型' }]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      style={{ width: '100%' }}
                      options={purchaseOptions.map((item) => ({
                        value: item.value,
                        label: item.label,
                        disabled: item.disabled,
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <Form.Item
                    label="自采设备成本（元）"
                    name="selfPurchasePrice"
                    rules={isSelfPurchase ? [{ required: true, message: '请输入自采设备成本' }] : []}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} precision={2} disabled={!isSelfPurchase} placeholder="请输入采购价格" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label="到期买断价（元）"
                    name="maturityBuyoutPrice"
                    rules={initData?.orderSummary.requiresManualMaturityBuyoutPrice ? [{ required: true, message: '请先填写到期买断价' }] : []}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} precision={2} placeholder="请输入到期买断价" />
                  </Form.Item>
                </Col>
                <Col span={4}>
                  <Form.Item
                    label="预收期数"
                    name="prepaymentPeriods"
                    rules={[
                      { required: true, message: '请输入预收期数' },
                      {
                        validator: (_, value) => {
                          if (value === undefined || value === null) {
                            return Promise.resolve();
                          }
                          if (value > maxPrepaymentPeriods) {
                            return Promise.reject(new Error(`预收期数不能超过${maxPrepaymentPeriods}期`));
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber style={{ width: '100%' }} min={0} max={maxPrepaymentPeriods} precision={0} addonAfter="期" />
                  </Form.Item>
                </Col>
                <Col span={5}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', height: 54 }}>
                    <Button type="primary" loading={calculating} onClick={handleCalculate}>利润预估测算</Button>
                    {onDecision && (
                      <Button style={{ background: '#f0b429', color: '#000', borderColor: '#f0b429' }} onClick={onDecision}>去决策</Button>
                    )}
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 8 }}>
                <Col span={6} />
                <Col span={5}>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>提示：该信息展示与采购人员沟通获取</Text>
                </Col>
                <Col span={4}>
                  <Text type="secondary" style={{ fontSize: 12, lineHeight: 1.4 }}>当前期购买价缺失时，将按该值反推全期设备回收残值</Text>
                </Col>
                <Col span={4} />
                <Col span={5} />
              </Row>
              <Form.Item hidden>
                <Button type="primary" loading={calculating} onClick={handleCalculate}>利润预估测算</Button>
              </Form.Item>
            </Form>
          </Card>

          <Card title="收益测算表" styles={{ body: { padding: 0 } }}>
            {calcData ? (
              <>
                <Table
                  rowKey="key"
                  columns={columns}
                  dataSource={rows}
                  pagination={false}
                  scroll={{ x: 1320 }}
                  size="middle"
                  bordered
                  rowClassName={(record) => (record.tone === 'profit' ? 'profit-estimation-profit-row' : '')}
                />
              </>
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="点击“利润预估测算”查看结果" />
            )}
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default ProfitEstimationModal;
